/**
 * SkyBand.tsx · 09_APP · OS-006 · capability C35
 * The top of Today: date, live Sacramento sky, greeting, quote of the day.
 * Approved design: 00_BRAIN/PROPOSALS/TODAY_PAGE_v2_2026-10-15.html
 */

import { useEffect, useMemo, useState, type ReactElement } from "react";
import { fetchSky, skyGradient, timeOfDay, CONDITION_WORD, type Sky } from "../lib/sky";

/* Verified sources, carried from the 2026-08-03 c2c-today design. */
const QUOTES: Array<[string, string]> = [
  ["The archer must know what he is seeking to hit; then he must aim and control the weapon by his skill. When a man does not know what harbour he is making for, no wind is the right wind.", "Seneca, Letters 71.3"],
  ["They live best who strive best to become as good as possible; and the pleasantest life is theirs who are conscious that they are growing in goodness.", "Socrates, in Xenophon's Memorabilia 4.8.6"],
  ["The mind converts every hindrance to its activity into an aid; that which is an obstacle on the road helps us on this road.", "Marcus Aurelius, Meditations 5.20"],
  ["First, say to yourself what you would be; and then do what you have to do.", "Epictetus, Discourses 3.23"],
  ["Don't wish it were easier, wish that you were better. Don't wish for fewer problems, wish for more skills.", "Jim Rohn, Leading an Inspired Life"],
  ["Every adversity brings with it the seed of an equivalent advantage.", "Napoleon Hill, Think and Grow Rich, 1937"],
  ["Failure is only the opportunity more intelligently to begin again. There is no disgrace in honest failure; there is disgrace in fearing to fail.", "Henry Ford, My Life and Work, 1922"],
  ["Such as are thy habitual thoughts, such also will be the character of thy mind; for the soul is dyed by the thoughts.", "Marcus Aurelius, Meditations 5.16"],
];

function greeting(d: Date): string {
  const h = d.getHours();
  if (h < 5) return "Good evening, Emily.";
  if (h < 12) return "Good morning, Emily.";
  if (h < 17) return "Good afternoon, Emily.";
  return "Good evening, Emily.";
}

function rand(seed: number) {
  let s = seed;
  return () => ((s = (s * 9301 + 49297) % 233280) / 233280);
}

export function SkyBand() {
  const now = new Date();
  const [sky, setSky] = useState<Sky>({ tod: timeOfDay(now), cond: "clear", tempF: null });

  useEffect(() => {
    let live = true;
    const load = () => fetchSky().then((s) => live && setSky(s));
    void load();
    const id = setInterval(load, 20 * 60 * 1000); // the sky changes; so does the band
    return () => { live = false; clearInterval(id); };
  }, []);

  const dayNo = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
  const [q, src] = QUOTES[dayNo % QUOTES.length] ?? QUOTES[0]!;
  const darkText = sky.tod === "day" && (sky.cond === "clear" || sky.cond === "fog");

  const deco = useMemo(() => {
    const r = rand(dayNo);
    const out: ReactElement[] = [];
    const { tod, cond } = sky;
    if (tod === "night" && (cond === "clear" || cond === "cloudy"))
      for (let i = 0; i < 40; i++)
        out.push(<i key={"s" + i} className="star" style={{ left: `${r() * 100}%`, top: `${r() * 70}%` }} />);
    if (cond === "clear" || cond === "cloudy") {
      const night = tod === "night";
      const pos = tod === "dawn" ? { left: "8%", top: "62%" } : tod === "dusk" ? { right: "8%", top: "62%" } : { right: "3%", top: night ? "-8px" : "-14px" };
      const col = tod === "day" ? "#FFE89A" : night ? "#F4F1E6" : "#FFC489";
      const size = night ? 38 : 56;
      out.push(
        <i key="orb" className="sunorb" style={{
          ...pos, width: size, height: size, background: col,
          boxShadow: night ? "0 0 30px 6px rgba(244,241,230,.35)" : `0 0 70px 26px ${col}88`,
          opacity: cond === "cloudy" ? 0.55 : 1,
        }} />,
      );
    }
    if (cond !== "clear" && cond !== "fog")
      for (let i = 0; i < (cond === "cloudy" ? 5 : 6); i++)
        out.push(<i key={"c" + i} className="cloud" style={{ left: `${i * 19 - 4}%`, top: `${10 + (i % 3) * 14}%`, width: 120 + (i % 2) * 60, height: 30, opacity: tod === "night" ? 0.35 : 0.8 }} />);
    if (cond === "rain" || cond === "storm")
      for (let i = 0; i < 60; i++)
        out.push(<i key={"d" + i} className="drop" style={{ left: `${r() * 100}%`, animationDelay: `${r()}s` }} />);
    if (cond === "fog")
      for (let i = 0; i < 4; i++) out.push(<i key={"f" + i} className="fog" style={{ top: 30 + i * 40 }} />);
    return out;
  }, [sky, dayNo]);

  return (
    <div className={darkText ? "band dark-text" : "band"} style={{ background: skyGradient(sky.tod, sky.cond) }}>
      <div className="layer" aria-hidden="true">
        {deco}
        <svg className="hills" viewBox="0 0 1200 70" preserveAspectRatio="none">
          <path d="M0 40 C200 10 380 34 560 22 C760 8 900 34 1200 16 L1200 70 L0 70Z" fill="rgba(10,30,55,.30)" />
          <path d="M0 58 C220 40 420 56 640 46 C860 36 1000 54 1200 44 L1200 70 L0 70Z" fill="rgba(10,30,55,.45)" />
        </svg>
      </div>
      <div className="over">
        <div className="date">
          <span>{now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="wx">
            Sacramento · {CONDITION_WORD[sky.cond]}
            {sky.tempF != null ? ` · ${Math.round(sky.tempF)}°F` : ""}
          </span>
        </div>
        <h1>{greeting(now)}</h1>
        <div className="q">
          &ldquo;{q}&rdquo;<b>{src}</b>
        </div>
      </div>
    </div>
  );
}
